import type { Progress, Statistics } from '../types'

/**
 * ProgressTracker - 进度追踪器
 * 负责记录答题情况、计算掌握度、追踪薄弱项
 */
export class ProgressTracker {
  private progress: Map<string, Progress> = new Map()

  /**
   * 记录一次答题
   *
   * 注意: 首次答题也走 calculateMastery 公式 (避免硬编码 magic number).
   * 1 次答对: accuracy=1, baseScore=80, bonus=5, total=85 — 立即过 60 阈值,
   * 让"答完一节就看到反馈"这件事符合直觉.
   */
  recordAttempt(cardId: string, isCorrect: boolean): void {
    const existing = this.progress.get(cardId)

    if (existing) {
      existing.attempts++
      if (isCorrect) {
        existing.correctCount++
      }
      existing.lastAttempt = Date.now()
      existing.mastery = this.calculateMastery(existing)
    } else {
      const newProgress: Progress = {
        cardId,
        attempts: 1,
        correctCount: isCorrect ? 1 : 0,
        lastAttempt: Date.now(),
        mastery: 0,
      }
      this.progress.set(cardId, newProgress)
      // 用公式重新算, 避免首次答题走"魔法值"分支
      newProgress.mastery = this.calculateMastery(newProgress)
    }
  }

  /**
   * 计算掌握度 (0-100)
   * 基于正确率和尝试次数
   *
   * 边界: 0 正确率时 mastery=0 (防止纯答错也能攒 mastery 的诡异场景)
   */
  private calculateMastery(progress: Progress): number {
    const { attempts, correctCount } = progress
    if (attempts === 0) return 0
    if (correctCount === 0) return 0

    const accuracy = correctCount / attempts
    // 尝试越多且正确率越高，掌握度越高
    const attemptBonus = Math.min(attempts * 5, 20) // 最多 20 分 bonus
    const baseScore = accuracy * 80 // 最高 80 分

    return Math.min(Math.round(baseScore + attemptBonus), 100)
  }

  /**
   * 获取某张卡片的掌握度
   */
  getMastery(cardId: string): number {
    return this.progress.get(cardId)?.mastery ?? 0
  }

  /**
   * 直接设置某张卡片的掌握度（用于解锁章节等管理操作）
   */
  setMastery(cardId: string, mastery: number): void {
    const existing = this.progress.get(cardId)
    if (existing) {
      existing.mastery = Math.min(Math.max(mastery, 0), 100)
    } else {
      this.progress.set(cardId, {
        cardId,
        attempts: 0,
        correctCount: 0,
        lastAttempt: Date.now(),
        mastery: Math.min(Math.max(mastery, 0), 100),
      })
    }
  }

  /**
   * 获取所有进度
   */
  getAllProgress(): Map<string, Progress> {
    return new Map(this.progress)
  }

  /**
   * 获取统计数据
   */
  getStatistics(): Statistics {
    const allProgress = Array.from(this.progress.values())
    const totalCards = allProgress.length
    const completedCards = allProgress.filter(p => p.mastery >= 60).length
    const totalAttempts = allProgress.reduce((sum, p) => sum + p.attempts, 0)
    const totalCorrect = allProgress.reduce((sum, p) => sum + p.correctCount, 0)
    const correctRate = totalAttempts > 0 ? totalCorrect / totalAttempts : 0

    // 简单薄弱项分析：按卡片类型分组（需要 card 数据）
    const weaknessAreas: { category: string; errorCount: number }[] = []

    return {
      totalCards,
      completedCards,
      totalAttempts,
      correctRate,
      weaknessAreas,
      studyTimeMs: 0, // 需要外部追踪
    }
  }

  /**
   * 清空所有进度（用于重置/清空学习数据）
   * 不影响 settings / oracle / guestBook 等其他存档字段
   */
  reset(): void {
    this.progress = new Map()
  }

  /**
   * 从存档恢复
   */
  loadFromSave(progressData: Record<string, Progress>): void {
    this.progress = new Map(Object.entries(progressData))
  }

  /**
   * 导出为存档格式
   */
  toSaveData(): Record<string, Progress> {
    return Object.fromEntries(this.progress)
  }
}
